class DynamicCarousel extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.onEnterViewport();
          }
        });
      },
      {
        threshold: 0.1, 
      }
    );

    this.observer.observe(this);
  }

  onEnterViewport() {
    if (this.hasInitialized) return;

    this.updateCarousel = this.updateCarousel.bind(this);
    this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);

    this.position = parseInt(this.getAttribute("start-index"));
    this.container = this.querySelector("ul.carousel__container");
    this.items = Array.from(this.querySelectorAll(".carousel__container li"));
    this.deferredMedias = Array.from(
      this.container.querySelectorAll("deferred-media")
    );

    this.totalItems = parseInt(this.getAttribute("item-size"));
    this.isInfinite = this.getAttribute("is-infinite") === "true";
    this.isAutoplay = this.getAttribute("is-autoplay") === "true";
    this.autoplayInterval = parseInt(this.getAttribute("autoplay-interval"));
    this.isAnimating = false;
    this.transitionEnabled = true;
    this.cloneCount = 2;

    this.nextButton = this.querySelector('button[name="next"]');
    this.previousButton = this.querySelector('button[name="previous"]');

    this.nextButton.addEventListener("click", this.onClickButton.bind(this));
    this.previousButton.addEventListener(
      "click",
      this.onClickButton.bind(this)
    );

    this.dynamicSlider = this.querySelector("dynamic-slider");
    this.items.forEach((item) =>
      item.addEventListener("click", this.openModal.bind(this))
    );

    if (this.isAutoplay) {
      this.addEventListener("mouseenter", this.stopAutoPlay.bind(this));
      this.addEventListener("mouseleave", this.startAutoPlay.bind(this));

      this.nextButton.addEventListener(
        "mouseenter",
        this.stopAutoPlay.bind(this)
      );
      this.nextButton.addEventListener(
        "mouseleave",
        this.startAutoPlay.bind(this)
      );
      this.previousButton.addEventListener(
        "mouseenter",
        this.stopAutoPlay.bind(this)
      );
      this.previousButton.addEventListener(
        "mouseleave",
        this.startAutoPlay.bind(this)
      );
    }

    this.init();
    this.hasInitialized = true;
  }

  init() {
    this.setupItems();
    document.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") this.prev();
      if (e.key === "ArrowRight") this.next();
    });

    let touchStartX = 0;
    this.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.touches[0].clientX;
      },
      { passive: true }
    );

    this.addEventListener(
      "touchend",
      (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX - touchEndX;

        if (Math.abs(diff) > 50) {
          if (diff > 0) this.next();
          else this.prev();
        }
      },
      { passive: true }
    );

    this.addEventListener("carouselModalClosed", (e) => {
      if (e.detail.isClose) {
        window.pauseAllMedia?.();
      }
    });
    this.startAutoPlay();
  }

  updateCarousel(enableTransition = true) {
    this.handleTransitionEnd();
    this.container.style.transition = enableTransition
      ? "transform 0.5s linear"
      : "none";

    this.style.setProperty("--position", this.position);
    if (!this.isInfinite) {
      this.previousButton.disabled = this.position <= this.cloneCount + 1;
      this.nextButton.disabled =
        this.position >= this.totalItems + this.cloneCount;
    }

    window.pauseAllMedia?.();
    this.deferredMedias.forEach((media) => media.removeAttribute("loaded"));

    const media =
      this.items[this.position - 1]?.querySelector("deferred-media");
    if (media) {
      media?.setAttribute("autoplay", true);
      media?.loadContent();
      this.scrollIntoView?.({ block: "nearest", behavior: "auto" });
    }
    this.items.forEach((item, index) => {
      const itemPosition = index + 1;
      let diff = this.position - itemPosition;
      if (this.isInfinite || this.isAutoplay) {
        const totalOriginalItems = this.totalItems;
        if (diff < -totalOriginalItems / 2) {
          diff += totalOriginalItems;
        } else if (diff > totalOriginalItems / 2) {
          diff -= totalOriginalItems;
        }
      }

      item.style.transform = `translateX(calc(var(--carousel-item-width) * ${
        diff * -1
      }))`;
      const visible = Math.abs(diff) <= 2;
      const scale = visible ? 1 + 0.5 * (1 - Math.abs(diff) / 2) : 1;

      item.style.transform = `translateX(calc(var(--carousel-item-width) * ${
        diff * -1
      }))`;
      item.style.scale = scale;
      item.style.opacity = visible ? "1" : "0";
      item.style.visibility = visible ? "visible" : "hidden";
    });

    setTimeout(() => {
      this.isAnimating = false;
    }, 500);
  }

  openModal(e) {
    this.stopAutoPlay();
    const currentId = e.currentTarget.dataset.id;
    this.dynamicSlider.classList.remove("modal-close");
    this.dynamicSlider.goToSlide(currentId, "next");
  }

  handleTransitionEnd() {
    if (!this.isInfinite || !this.isAutoplay) return;
    if (this.position >= this.totalItems + this.cloneCount) {
      this.transitionEnabled = false;
      this.position = this.cloneCount;
      this.updateCarousel(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.transitionEnabled = true;
        });
      });
    } else if (this.position <= this.cloneCount - 1) {
      this.transitionEnabled = false;
      this.position = this.totalItems + this.cloneCount - 1;
      this.updateCarousel(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.transitionEnabled = true;
        });
      });
    }

    this.isAnimating = false;
  }

  onClickButton(event) {
    this.stopAutoPlay();

    if (event.currentTarget.name === "next") {
      this.next();
    } else {
      this.prev();
    }
  }

  setupItems() {
    this.items.forEach((item, index) => {
      item.style.setProperty("--offset", index + 1);
      item.addEventListener("click", () => {
        if (!this.isAnimating) {
          const clickedPosition = index + 1;
          const diff = clickedPosition - this.position;
          if (diff <= 0) {
            for (let i = 0; i < Math.abs(diff); i++) this.prev();
          } else if (diff > 0) {
            for (let i = 0; i < diff; i++) this.next();
          }
        }
      });
    });

    this.position += 2;
    this.updateCarousel();
  }

  autoplay() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    if (this.isInfinite) {
      this.position++;
    } else {
      if (this.isAutoplay) {
        if (this.position >= this.totalItems + this.cloneCount) {
          this.position = this.cloneCount;
        } else if (this.position <= this.cloneCount - 1) {
          this.position = this.totalItems + this.cloneCount - 1;
        } else {
          this.position++;
        }
      }
    }
    this.updateCarousel();
  }

  next() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    if (this.isInfinite || this.isAutoplay) {
      if (this.position >= this.totalItems + this.cloneCount - 1) {
        this.position = this.cloneCount;
      } else {
        this.position++;
      }
    } else {
      if (this.position <= this.totalItems + this.cloneCount - 1) {
        this.position++;
      }
    }

    this.updateCarousel();
  }

  prev() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    if (this.isInfinite || this.isAutoplay) {
      if (this.position <= this.cloneCount) {
        this.position = this.totalItems + this.cloneCount - 1;
      } else {
        this.position--;
      }
    } else {
      if (this.position >= this.cloneCount + 2) {
        this.position--;
      }
    }

    this.updateCarousel();
  }

  startAutoPlay() {
    if (!this.isAutoplay) return;
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
    }
    this.updateCarousel();
    this.autoPlayInterval = setInterval(
      () => this.autoplay(),
      this.autoplayInterval
    );
  }

  stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  disconnectedCallback() {
    this.stopAutoPlay();
  }
}

if (!customElements.get("dynamic-carousel")) {
  customElements.define("dynamic-carousel", DynamicCarousel);
}

// ========================================================================

class DynamicSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isAnimating = false;

    this.button = this.querySelector('button[name="modal-button"]');
    this.button?.addEventListener("click", this.closeModal.bind(this));
    this.init();
  }

  init() {
    this.mainContainer = this.querySelector(".slider-main");
    this.mainSlides = Array.from(this.mainContainer.querySelectorAll(".slide"));
    this.totalSlides = this.mainSlides.length;

    this.thumbnailContainer = this.querySelector(".slider-thumbnails");
    this.thumbnails = Array.from(
      this.thumbnailContainer.querySelectorAll(".thumbnail")
    );

    this.splideEnabled();
  }

  closeModal() {
    this.classList.add("modal-close");

    const modalClosedEvent = new CustomEvent("carouselModalClosed", {
      detail: { isClose: true },
      bubbles: true, // Enable bubbling
    });
    this.dispatchEvent(modalClosedEvent);
  }

  splideEnabled() {
    document.addEventListener("DOMContentLoaded", function () {
      const main = new Splide("#main-carousel", {
        type: "fade",
        rewind: true,
        pagination: false,
        arrows: false,
      });

      const thumbnails = new Splide("#thumbnail-carousel", {
        gap: 10,
        rewind: true,
        pagination: false,
        isNavigation: true,
        breakpoints: {
          600: {
            fixedWidth: 60,
            fixedHeight: 44,
          },
        },
      });

      main.sync(thumbnails);
      main.mount();
      thumbnails.mount();
    });
  }

  goToSlide(currentId, direction) {
    const currentIndex = this.mainSlides.findIndex(
      (thumbnail) => thumbnail.dataset.main === currentId
    );

    this.mainSlides[this.currentIndex].classList.remove(
      "is-active",
      "is-visible"
    );
    this.thumbnails[this.currentIndex].classList.remove(
      "is-active",
      "is-visible"
    );

    this.mainSlides[currentIndex].classList.add("is-active", "is-visible");
    this.thumbnails[currentIndex].classList.add("is-active", "is-visible");

    this.currentIndex = currentIndex;

    window.pauseAllMedia?.();
    const media =
      this.mainSlides[currentIndex]?.querySelector("deferred-media");
    media?.setAttribute("autoplay", true);
    media?.loadContent();
  }

  next() {
    if (this.isAnimating) return;
    const nextIndex = (this.currentIndex + 1) % this.totalSlides;
    this.goToSlide(nextIndex, "next");
  }

  prev() {
    if (this.isAnimating) return;
    const prevIndex =
      (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
    this.goToSlide(prevIndex, "prev");
  }

  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
  }

  handleTouchEnd(e) {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = this.touchStartX - touchEndX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) this.next();
      else this.prev();
    }
  }

  handleKeyNavigation(e) {
    if (e.key === "ArrowLeft") this.prev();
    else if (e.key === "ArrowRight") this.next();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeyNavigation);
  }
}

if (!customElements.get("dynamic-slider")) {
  customElements.define("dynamic-slider", DynamicSlider);
}
